import { useEffect, useRef, useContext, useState } from 'react';
import { trackEvent, TrackingContext } from '@gjensidige/core-analytics';

import styles from '../video.module.css';

// Define type for YT global object
type YT = {
  Player: new (
    elementId: string,
    options: {
      videoId: string;
      events: { [key: string]: (event: any) => void };
      host?: string;
    }
  ) => void;
  PlayerState: {
    PLAYING: number;
    PAUSED: number;
  };
};

// Define global YT object
declare global {
  interface Window {
    YT: YT;
    onYouTubeIframeAPIReady: () => void;
  }
}

type Props = {
  videoId: string;
  title?: string;
};

const TRACKING = {
  eventAction: 'click',
  eventCategory: 'video',
  eventLabel: 'youtube-play'
};

const YOUTUBE_ID_REGEX =
  /(youtu\.be\/|youtube\.com\/(watch\?(.*&)?v=|(embed|v)|live\/))([^?&"'>]+)/; // From https://gist.github.com/afeld/1254889

const loadYouTubeAPI = (() => {
  let promise: Promise<void> | null = null;
  return () => {
    if (!promise) {
      promise = new Promise((resolve) => {
        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        if (firstScriptTag?.parentNode) {
          firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
        }
        window.onYouTubeIframeAPIReady = () => resolve();
      });
    }
    return promise;
  };
})();

const YouTubePlayer = ({ videoId, title }: Props) => {
  const youTubeId = videoId?.slice(0, 300)?.match(YOUTUBE_ID_REGEX)?.[5]; // Slice to ensure we can not get a too ong string slowing down the regex
  const playerRef = useRef<HTMLDivElement>(null);
  const track = useContext(TrackingContext);
  const [isYouTubeAPIAvailable, setIsYouTubeAPIAvailable] =
    useState<boolean>(false);

  useEffect(() => {
    // Load YouTube IFrame API when component mounts

    loadYouTubeAPI().then(() => {
      setIsYouTubeAPIAvailable(true);
      if (playerRef.current) {
        new window.YT.Player(playerRef.current?.id, {
          videoId: youTubeId || '',
          host: 'https://www.youtube-nocookie.com',
          events: {
            onStateChange: onPlayerStateChange
          }
        });
      }
    });

    const onPlayerStateChange = (event: any) => {
      if (playClicked) return;

      if (event.data === window.YT.PlayerState.PLAYING) {
        playClicked = true;
        trackEvent(TRACKING, track.options);
      }
    };

    let playClicked = false;
  }, [videoId, youTubeId, track.options, isYouTubeAPIAvailable]);

  return (
    <div className={styles.videoContainer}>
      {isYouTubeAPIAvailable ? (
        <div
          id={`player-${youTubeId}`}
          ref={playerRef}
          aria-label={title ? `Video: ${title}` : 'video'}
        />
      ) : (
        <iframe
          aria-label={title ? `Video: ${title}` : 'video'}
          src={`https://www.youtube-nocookie.com/embed/${youTubeId}`}
        />
      )}
    </div>
  );
};

export default YouTubePlayer;
